import { type ReactNode, useEffect, useRef } from "react";
import { Sheet, SheetContent } from "@volca/ui/components/ui/sheet";
import { cn } from "@volca/ui/lib/utils";
import { useShowSource } from "@/profitability/sources/source-context";

/** Room for a record's history table: a period, its figures and their change. */
const RECORD_SHEET_WIDTH = "max-w-xl max-sm:w-full";

/**
 * A record (a job, a technician) in a sheet floating over the page's right
 * edge. Not modal and without a backdrop: the list behind stays usable, so
 * opening another row swaps the record in place. Escape or its close button
 * closes it. The app's own right panel stays reserved for the chat.
 *
 * Its look is chosen here, not by the sheet's default: the inset variant
 * (off the window's edges, rounded, no border) at `RECORD_SHEET_WIDTH`.
 */
export const RecordSheet = ({
  open,
  label,
  onClose,
  children,
  returnFocus,
}: {
  open: boolean;
  /** Names the sheet for assistive technology: "Job 48218". */
  label: string;
  onClose: () => void;
  children: ReactNode;
  returnFocus?: HTMLElement | null;
}) => {
  const focusTarget = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) focusTarget.current = returnFocus ?? null;
  }, [open, returnFocus]);
  // The source dialog opens over the sheet; its Escape closes only it.
  const source = useShowSource();
  return (
    <Sheet
      open={open}
      modal={false}
      disablePointerDismissal
      onOpenChange={(next) => !next && !source?.isOpen && onClose()}
    >
      <SheetContent
        variant="inset"
        backdrop={false}
        showCloseButton={false}
        aria-label={label}
        className={cn(RECORD_SHEET_WIDTH, { "pointer-events-none": !open })}
        inert={!open}
        aria-hidden={!open || undefined}
        finalFocus={() =>
          focusTarget.current?.isConnected ? focusTarget.current : true
        }
      >
        {children}
      </SheetContent>
    </Sheet>
  );
};
